import { useState, useRef } from 'react';
import {
  Search,
  ShieldBan,
  ShieldX,
  Plus,
  Trash2,
  Loader2,
  ChevronDown,
  X,
  PhoneOff,
  User,
  Building2,
  AlertCircle,
} from 'lucide-react';
import { parseDisplayName, formatDate } from '@/lib/utils';
import {
  usePhoneBlacklist,
  useAddPhoneToBlacklist,
  useRemovePhoneFromBlacklist,
} from '@/api/usePhoneBlacklist';
import type { PhoneBlacklistEntry } from '@/api/usePhoneBlacklist';
import styles from './PhoneBlacklist.module.css';

/**
 * Loose phone validation: allows optional leading +, digits, spaces, dashes, parentheses.
 * Requires at least 7 digits total — covers most international numbers without being strict
 * about country-specific formats. Real format is enforced server-side.
 */
const PHONE_RE = /^[+]?[\d\s\-()]{7,}$/;
function isValidPhone(value: string): boolean {
  if (!PHONE_RE.test(value)) return false;
  const digits = value.replace(/\D/g, '');
  return digits.length >= 7;
}

type FilterType = 'all' | 'linked' | 'standalone';

const FILTER_OPTIONS: { value: FilterType; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'linked', label: 'With Contact' },
  { value: 'standalone', label: 'Without Contact' },
];

export const PhoneBlacklist = () => {
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [offset, setOffset] = useState(0);
  const [showAddForm, setShowAddForm] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [reasonInput, setReasonInput] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState<number | null>(null);
  const prevFilterRef = useRef('');

  const { data, isLoading, isFetching } = usePhoneBlacklist(search, offset);
  const addMutation = useAddPhoneToBlacklist();
  const removeMutation = useRemovePhoneFromBlacklist();

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const stats = data?.stats ?? { total: 0, linkedToPerson: 0, orphan: 0 };

  // Reset offset when filters change
  const currentKey = `${search}|${filterType}`;
  if (currentKey !== prevFilterRef.current) {
    prevFilterRef.current = currentKey;
    if (offset !== 0) setOffset(0);
  }

  const filteredItems = items.filter((item) => {
    if (filterType === 'linked') return item.person !== null;
    if (filterType === 'standalone') return item.person === null;
    return true;
  });

  const handleAdd = async () => {
    const phones = phoneInput
      .split(/[,\n]/)
      .map((p) => p.trim())
      .filter((p) => p.length > 0);

    if (phones.length === 0) {
      setPhoneError('Enter at least one phone number');
      return;
    }

    const invalid = phones.filter((p) => !isValidPhone(p));
    if (invalid.length > 0) {
      setPhoneError(
        invalid.length === 1
          ? `"${invalid[0]}" is not a valid phone number`
          : `${invalid.length} numbers have an invalid format. Phones need at least 7 digits and may start with "+".`,
      );
      return;
    }

    setPhoneError(null);
    try {
      await addMutation.mutateAsync({
        phoneNumbers: phones,
        reason: reasonInput || undefined,
      });
      // Only clear on success — fields preserved on error so the user can fix and retry
      setPhoneInput('');
      setReasonInput('');
      setShowAddForm(false);
    } catch {
      // toast handled in hook; fields are preserved for retry
    }
  };

  const handleRemove = async (entry: PhoneBlacklistEntry) => {
    try {
      await removeMutation.mutateAsync({ ids: [entry.id] });
      setConfirmRemoveId(null);
    } catch {
      // toast handled in hook
    }
  };

  const hasMore = items.length + offset < total;

  const getContactDisplay = (entry: PhoneBlacklistEntry) => {
    if (!entry.person) return null;
    return {
      name: entry.person.fullName || entry.person.phoneNumber || 'Unknown',
      business: entry.person.place
        ? (parseDisplayName(entry.person.place.displayName as string | null)?.text ?? '—')
        : '—',
    };
  };

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerText}>
            <h1 className={styles.title}>Phone Blacklist</h1>
            <p className={styles.subtitle}>
              Manage blocked phone numbers. Blacklisted numbers will not receive WhatsApp outreach.
            </p>
          </div>
          <button
            onClick={() => {
              setShowAddForm((open) => !open);
              setPhoneError(null);
            }}
            className={styles.addBtn}
            aria-expanded={showAddForm}
            aria-controls="phone-blacklist-add-form"
          >
            {showAddForm ? (
              <>
                <X className={styles.addBtnIcon} aria-hidden="true" /> Close
              </>
            ) : (
              <>
                <Plus className={styles.addBtnIcon} aria-hidden="true" /> Add to Blacklist
              </>
            )}
          </button>
        </div>

        {/* Add form */}
        {showAddForm && (
          <div id="phone-blacklist-add-form" className={styles.addCard}>
            <div className={styles.addCardHeader}>
              <ShieldBan className={styles.addCardIcon} aria-hidden="true" />
              <span className={styles.addCardTitle}>Add Phone Numbers to Blacklist</span>
            </div>
            <label htmlFor="phone-blacklist-numbers" className={styles.srOnly}>
              Phone numbers to blacklist (required)
            </label>
            <textarea
              id="phone-blacklist-numbers"
              className={`${styles.phoneTextarea} ${phoneError ? styles.fieldError : ''}`}
              placeholder="Enter phone numbers (one per line or comma-separated) — e.g. +1234567890, +9876543210"
              value={phoneInput}
              onChange={(e) => {
                setPhoneInput(e.target.value);
                if (phoneError) setPhoneError(null);
              }}
              rows={3}
              aria-required="true"
              aria-invalid={phoneError ? true : undefined}
              aria-describedby={phoneError ? 'phone-blacklist-numbers-error' : undefined}
            />
            {phoneError && (
              <div id="phone-blacklist-numbers-error" role="alert" className={styles.fieldErrorMsg}>
                <AlertCircle className={styles.fieldErrorIcon} aria-hidden="true" />
                <span>{phoneError}</span>
              </div>
            )}
            <label htmlFor="phone-blacklist-reason" className={styles.srOnly}>
              Reason (optional)
            </label>
            <input
              id="phone-blacklist-reason"
              type="text"
              className={styles.reasonInput}
              placeholder="Reason (optional) — e.g. Requested removal, spam, invalid"
              value={reasonInput}
              onChange={(e) => setReasonInput(e.target.value)}
            />
            <div className={styles.addCardActions}>
              <span className={styles.addCardHint}>
                {phoneInput.split(/[,\n]/).filter((p) => p.trim().length > 0).length} number(s)
                detected
              </span>
              <button
                type="button"
                onClick={handleAdd}
                disabled={addMutation.isPending}
                className={styles.blacklistBtn}
                aria-busy={addMutation.isPending}
              >
                {addMutation.isPending ? (
                  <Loader2
                    className={`${styles.addBtnIcon} ${styles.spinner}`}
                    aria-hidden="true"
                  />
                ) : (
                  <ShieldBan className={styles.addBtnIcon} aria-hidden="true" />
                )}
                {addMutation.isPending ? 'Blacklisting…' : 'Blacklist'}
              </button>
            </div>
          </div>
        )}

        {/* Stats cards */}
        <div className={styles.statsGrid}>
          <div className={`${styles.statCard} ${styles.statTotal}`}>
            <ShieldBan className={styles.statIcon} />
            <div className={styles.statValue}>{stats.total}</div>
            <div className={styles.statLabel}>Total Blocked</div>
          </div>
          <div className={`${styles.statCard} ${styles.statLinked}`}>
            <User className={styles.statIcon} />
            <div className={styles.statValue}>{stats.linkedToPerson}</div>
            <div className={styles.statLabel}>Linked to Contact</div>
          </div>
          <div className={`${styles.statCard} ${styles.statStandalone}`}>
            <PhoneOff className={styles.statIcon} />
            <div className={styles.statValue}>{stats.orphan}</div>
            <div className={styles.statLabel}>Standalone</div>
          </div>
        </div>

        {/* Filters */}
        <div className={styles.filtersCard}>
          <div className={styles.filterRow}>
            <div className={styles.searchWrapper}>
              <label htmlFor="phone-blacklist-search" className={styles.srOnly}>
                Search blacklist
              </label>
              <Search className={styles.searchIcon} aria-hidden="true" />
              <input
                id="phone-blacklist-search"
                type="search"
                placeholder="Search by phone, name, or business..."
                className={styles.searchInput}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className={styles.filterBtns}>
              {FILTER_OPTIONS.map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => setFilterType(value)}
                  className={`${styles.filterBtn} ${filterType === value ? styles.filterBtnActive : ''}`}
                >
                  {label}
                  {value === 'linked' && (
                    <span className={styles.filterBtnCount}>{stats.linkedToPerson}</span>
                  )}
                  {value === 'standalone' && (
                    <span className={styles.filterBtnCount}>{stats.orphan}</span>
                  )}
                </button>
              ))}
            </div>
          </div>
          <div className={styles.filterMeta}>
            <span className={styles.filterCount}>
              {total} result{total !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Table */}
        <div className={styles.tableCard}>
          {isLoading ? (
            <div className={styles.loadingState}>
              <Loader2 className={styles.spinnerLg} />
              <span className={styles.loadingText}>Loading phone blacklist...</span>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className={styles.emptyState}>
              <ShieldBan className={styles.emptyIcon} />
              <p className={styles.emptyTitle}>
                {search || filterType !== 'all'
                  ? 'No results match your filters'
                  : 'Your phone blacklist is empty'}
              </p>
              <p className={styles.emptyDesc}>
                {search || filterType !== 'all'
                  ? 'Try adjusting your search or filter.'
                  : 'Blocked phone numbers appear here. Add numbers manually or block them from the WhatsApp inbox.'}
              </p>
            </div>
          ) : (
            <>
              <div className={styles.tableWrapper}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Phone Number</th>
                      <th className={styles.hiddenSm}>Contact</th>
                      <th className={styles.hiddenMd}>Business</th>
                      <th className={styles.hiddenMd}>Reason</th>
                      <th className={styles.hiddenSm}>Blocked On</th>
                      <th style={{ width: '3rem' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((entry) => {
                      const contact = getContactDisplay(entry);
                      return (
                        <tr key={entry.id}>
                          <td>
                            <div className={styles.phoneNumber}>{entry.phoneNumber}</div>
                            {entry.blacklistedBy && (
                              <div className={styles.blockedBy}>by {entry.blacklistedBy.email}</div>
                            )}
                          </td>
                          <td className={styles.hiddenSm}>
                            {contact ? (
                              <div className={styles.contactName}>{contact.name}</div>
                            ) : (
                              <span className={styles.noContact}>—</span>
                            )}
                          </td>
                          <td className={styles.hiddenMd}>
                            {contact ? (
                              <span className={styles.businessName}>
                                <Building2 className={styles.businessIcon} />
                                {contact.business}
                              </span>
                            ) : (
                              <span className={styles.noContact}>—</span>
                            )}
                          </td>
                          <td className={`${styles.reasonCell} ${styles.hiddenMd}`}>
                            {entry.reason || '—'}
                          </td>
                          <td className={`${styles.dateCell} ${styles.hiddenSm}`}>
                            {formatDate(entry.blacklistedAt)}
                          </td>
                          <td>
                            {confirmRemoveId === entry.id ? (
                              <div className={styles.confirmRemove}>
                                <button
                                  onClick={() => handleRemove(entry)}
                                  className={styles.confirmYes}
                                  title="Confirm remove"
                                  aria-label={`Confirm remove ${entry.phoneNumber} from blacklist`}
                                  disabled={removeMutation.isPending}
                                >
                                  {removeMutation.isPending ? (
                                    <Loader2
                                      className={`${styles.actionIcon} ${styles.spinner}`}
                                      aria-hidden="true"
                                    />
                                  ) : (
                                    <ShieldX className={styles.actionIcon} aria-hidden="true" />
                                  )}
                                </button>
                                <button
                                  onClick={() => setConfirmRemoveId(null)}
                                  className={styles.confirmNo}
                                  title="Cancel"
                                  aria-label="Cancel remove"
                                  disabled={removeMutation.isPending}
                                >
                                  <X className={styles.actionIcon} aria-hidden="true" />
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setConfirmRemoveId(entry.id)}
                                className={styles.removeBtn}
                                title="Remove from blacklist"
                                aria-label={`Remove ${entry.phoneNumber} from blacklist`}
                              >
                                <Trash2 className={styles.actionIcon} aria-hidden="true" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {hasMore && (
                <div className={styles.loadMoreWrapper}>
                  <button
                    onClick={() => setOffset((prev) => prev + 20)}
                    disabled={isFetching}
                    className={styles.loadMoreBtn}
                  >
                    {isFetching ? (
                      <Loader2
                        className={`${styles.loadMoreIcon} ${styles.spinner}`}
                        aria-hidden="true"
                      />
                    ) : (
                      <ChevronDown className={styles.loadMoreIcon} aria-hidden="true" />
                    )}
                    Load more ({Math.max(total - (offset + items.length), 0)} remaining)
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
